import { useCallback } from "react";
import { CellValueChangedEvent } from "ag-grid-community";

import { showWarning } from "src/utils/toast/toastUtil";
import {
	getInspectionValueValidationMessage,
	isValidInspectionValue,
	normalizeInspectionValue,
} from "src/utils/inspection/inspectionValueUtils";

import {
	TQC2090_R01Dto,
	TQC2090_R01_VALUE_FIELDS,
} from "../Dto_TQC2090_R01";

const VALUE_FIELDS: (keyof TQC2090_R01Dto)[] = [...TQC2090_R01_VALUE_FIELDS];
const THICKNESS_VALUE_FIELDS: (keyof TQC2090_R01Dto)[] = [
	"thicknessFirstValue", "thicknessMiddleValue", "thicknessLastValue",
];

const NICKEL_INSPECT_TYPE = "FML_CR";
const NICKEL_APPEARANCE_OPTIONS = ["O", "X"];

interface UseTQC2090_R01CellChangeProps {
	updateTQC2090_R01List: (index: number, data: TQC2090_R01Dto) => void;
	originalTQC2090_R01List: TQC2090_R01Dto[];
}

/**
 * 니켈크롬 외관 검사값을 저장 형태로 맞춘다.
 * 소문자 o/x 는 대문자로 올리고, 키패드로 치기 쉬운 숫자 0 은 알파벳 O 로 본다.
 * (다른 검사유형은 손대지 않는다)
 */
const normalizeInspectValue = (row: TQC2090_R01Dto, inputValue: string): string => {
	if (row.specInspType !== NICKEL_INSPECT_TYPE) {
		return normalizeInspectionValue(row.valueType ?? "", inputValue);
	}
	const upperValue = inputValue.toUpperCase();
	return upperValue === "0" ? "O" : upperValue;
};

/**
 * 검사값 셀의 입력 검증 결과.
 * 기준값 대비 합·부는 판정하지 않는다. 형식만 본다. (설계 문서 §7)
 */
const validateInspectValue = (
	row: TQC2090_R01Dto,
	inputValue: string,
): { isValid: boolean; message: string } => {
	if (inputValue === "") {
		return { isValid: true, message: "" };
	}

	if (row.specInspType === NICKEL_INSPECT_TYPE) {
		const isValid = NICKEL_APPEARANCE_OPTIONS.includes(inputValue.toUpperCase());
		return { isValid, message: "외관 검사값은 O 또는 X만 입력할 수 있습니다." };
	}

	const valueType = row.valueType ?? "";
	return {
		isValid: isValidInspectionValue(valueType, inputValue),
		message: getInspectionValueValidationMessage(valueType),
	};
};

/**
 * 검사값 15칸과 비고의 값 변경을 스토어에 반영하는 훅.
 *
 * 편집 필드가 전부 원본과 같아지면 isModified 를 해제해 불필요한 저장을 막는다.
 */
export function useTQC2090_R01CellChange({
	updateTQC2090_R01List,
	originalTQC2090_R01List,
}: UseTQC2090_R01CellChangeProps) {
	const handleCellChange = useCallback(
		(event: CellValueChangedEvent<TQC2090_R01Dto>) => {
			const { data, colDef, newValue } = event;
			if (!data || !colDef.field) return;

			const field = colDef.field as keyof TQC2090_R01Dto;
			const isThicknessField = THICKNESS_VALUE_FIELDS.includes(field);
			const isValueField = VALUE_FIELDS.includes(field) || isThicknessField;
			const rawValue = String(newValue ?? "").trim();
			const validationRow = isThicknessField
				? { ...data, specInspType: "", valueType: data.thicknessValueType }
				: data;
			// 검증과 저장에 같은 값을 쓰도록 먼저 정규화한다.
			const inputValue = isValueField
				? normalizeInspectValue(validationRow, rawValue)
				: rawValue;

			const { isValid, message } = isValueField
				? validateInspectValue(validationRow, inputValue)
				: { isValid: true, message: "" };

			const updatedData: TQC2090_R01Dto = {
				...data,
				[field]: isValueField ? (isValid ? inputValue : "") : newValue,
				isModified: true,
			};

			const originalRow = originalTQC2090_R01List.find(
				(item) => item.valueIndex === data.valueIndex,
			);
			if (originalRow) {
				const editableFields: (keyof TQC2090_R01Dto)[] = [
					...VALUE_FIELDS, ...THICKNESS_VALUE_FIELDS, "remark",
				];
				const unchanged = editableFields.every(
					(key) => (originalRow[key] ?? "") === (updatedData[key] ?? ""),
				);
				if (unchanged) updatedData.isModified = false;
			}

			updateTQC2090_R01List(data.valueIndex, updatedData);

			// 잘못된 값은 비우고 해당 셀에 다시 입력 포커스를 둔다.
			if (!isValid && event.node.rowIndex !== null) {
				const rowIndex = event.node.rowIndex;
				showWarning(message);
				setTimeout(() => {
					event.api.ensureIndexVisible(rowIndex);
					event.api.setFocusedCell(rowIndex, colDef.field!);
					event.api.startEditingCell({ rowIndex, colKey: colDef.field! });
				}, 0);
			}
		},
		[updateTQC2090_R01List, originalTQC2090_R01List],
	);

	return { handleCellChange };
}
